import type { SupabaseClient } from "@supabase/supabase-js";
import { normalizeInvoice } from "./calculations";
import type {
  BillingProfile,
  Client,
  Invoice,
  LineItem,
  PaymentRecord,
  ReceiptAttachment,
  ReceiptMimeType,
} from "./types";

interface ProfileRow {
  id: string;
  owner_user_id: string;
  display_name: string;
  logo_path: string | null;
  address: string | null;
  email: string | null;
  phone: string | null;
  website: string | null;
  registration_number: string | null;
  invoice_prefix: string;
  default_payment_days: number;
  brand_colour: string | null;
  payment_instructions: string | null;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
}

interface ClientRow {
  id: string;
  owner_user_id: string;
  billing_profile_id: string;
  business_name: string;
  contact_name: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  registration_number: string | null;
  last_used_at: string | null;
  created_at: string;
  updated_at: string;
}

interface InvoiceRow {
  id: string;
  owner_user_id: string;
  billing_profile_id: string;
  number: string;
  status: Invoice["status"];
  issue_date: string;
  due_date: string;
  purchase_order_number: string | null;
  project_reference: string | null;
  currency: "HKD";
  language: Invoice["language"];
  paper_size: Invoice["paperSize"];
  issuer_snapshot: Invoice["issuer"];
  client_snapshot: Invoice["client"];
  discount: Invoice["discount"];
  subtotal_minor: number;
  discount_minor: number;
  total_minor: number;
  client_notes: string | null;
  internal_notes: string | null;
  payment_instructions: string | null;
  terms: string | null;
  authorised_name: string | null;
  paid_minor: number;
  balance_minor: number;
  created_at: string;
  updated_at: string;
  invoice_items: ItemRow[];
  payments: PaymentRow[];
  invoice_receipts: ReceiptRow[];
}

interface ItemRow {
  id: string;
  position: number;
  description: string;
  quantity: number | string;
  unit: string | null;
  rate_minor: number;
  amount_minor: number;
}

interface PaymentRow {
  id: string;
  amount_minor: number;
  payment_date: string;
  method: string | null;
  reference: string | null;
  note: string | null;
}

interface ReceiptRow {
  id: string;
  owner_user_id: string;
  billing_profile_id: string;
  invoice_id: string;
  storage_path: string;
  file_name: string;
  mime_type: ReceiptMimeType;
  file_size: number;
  position: number;
  created_at: string;
}

interface SuppressionRow {
  billing_profile_id: string;
  kind: "description" | "unit" | "client_notes" | "internal_notes";
  normalized_value: string;
}

function profileFromRow(row: ProfileRow): BillingProfile {
  return {
    id: row.id,
    ownerUserId: row.owner_user_id,
    displayName: row.display_name,
    logoPath: row.logo_path ?? "",
    address: row.address ?? "",
    email: row.email ?? "",
    phone: row.phone ?? "",
    website: row.website ?? "",
    registrationNumber: row.registration_number ?? "",
    invoicePrefix: row.invoice_prefix,
    defaultPaymentDays: row.default_payment_days,
    brandColour: row.brand_colour ?? "",
    paymentInstructions: row.payment_instructions ?? "",
    archivedAt: row.archived_at,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function clientFromRow(row: ClientRow): Client {
  return {
    id: row.id,
    ownerUserId: row.owner_user_id,
    billingProfileId: row.billing_profile_id,
    party: {
      name: row.business_name,
      contactName: row.contact_name ?? "",
      email: row.email ?? "",
      phone: row.phone ?? "",
      address: row.address ?? "",
      registrationNumber: row.registration_number ?? "",
    },
    lastUsedAt: row.last_used_at,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function invoiceFromRow(row: InvoiceRow): Invoice {
  const items: LineItem[] = [...(row.invoice_items ?? [])]
    .sort((a, b) => a.position - b.position)
    .map((item) => ({
      id: item.id,
      description: item.description,
      quantity: Number(item.quantity),
      unit: item.unit ?? "",
      rateMinor: item.rate_minor,
      amountMinor: item.amount_minor,
    }));
  const payments: PaymentRecord[] = (row.payments ?? []).map((payment) => ({
    id: payment.id,
    amountMinor: payment.amount_minor,
    paymentDate: payment.payment_date,
    method: payment.method ?? "",
    reference: payment.reference ?? "",
    note: payment.note ?? "",
  }));
  const receipts: ReceiptAttachment[] = [...(row.invoice_receipts ?? [])]
    .sort(
      (a, b) =>
        a.position - b.position || a.created_at.localeCompare(b.created_at),
    )
    .map((receipt) => ({
      id: receipt.id,
      ownerUserId: receipt.owner_user_id,
      billingProfileId: receipt.billing_profile_id,
      invoiceId: receipt.invoice_id,
      storagePath: receipt.storage_path,
      fileName: receipt.file_name,
      mimeType: receipt.mime_type,
      fileSize: receipt.file_size,
      position: receipt.position,
      createdAt: receipt.created_at,
    }));
  return normalizeInvoice({
    id: row.id,
    ownerUserId: row.owner_user_id,
    billingProfileId: row.billing_profile_id,
    number: row.number,
    status: row.status,
    issueDate: row.issue_date,
    dueDate: row.due_date,
    purchaseOrderNumber: row.purchase_order_number ?? "",
    projectReference: row.project_reference ?? "",
    currency: row.currency,
    language: row.language,
    paperSize: row.paper_size,
    issuer: row.issuer_snapshot,
    client: row.client_snapshot,
    items,
    discount: row.discount,
    subtotalMinor: row.subtotal_minor,
    discountMinor: row.discount_minor,
    totalMinor: row.total_minor,
    clientNotes: row.client_notes ?? "",
    internalNotes: row.internal_notes ?? "",
    paymentInstructions: row.payment_instructions ?? "",
    terms: row.terms ?? "",
    authorisedName: row.authorised_name ?? "",
    payments,
    receipts,
    paidMinor: row.paid_minor,
    balanceMinor: row.balance_minor,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  });
}

export async function loadCloudWorkspace(
  client: SupabaseClient,
): Promise<{
  profiles: BillingProfile[];
  clients: Client[];
  invoices: Invoice[];
  hiddenHistory: string[];
}> {
  const [profilesResult, clientsResult, invoicesResult, suppressionsResult] =
    await Promise.all([
    client.from("billing_profiles").select("*").order("created_at"),
    client.from("clients").select("*").order("updated_at", { ascending: false }),
    client
      .from("invoices")
      .select("*, invoice_items(*), payments(*), invoice_receipts(*)")
      .order("updated_at", { ascending: false }),
      client
        .from("invoice_text_suppressions")
        .select("billing_profile_id, kind, normalized_value"),
    ]);

  const error =
    profilesResult.error ??
    clientsResult.error ??
    invoicesResult.error ??
    suppressionsResult.error;
  if (error) throw error;

  return {
    profiles: (profilesResult.data as ProfileRow[]).map(profileFromRow),
    clients: (clientsResult.data as ClientRow[]).map(clientFromRow),
    invoices: (invoicesResult.data as InvoiceRow[]).map(invoiceFromRow),
    hiddenHistory: (suppressionsResult.data as SuppressionRow[]).map(
      (row) =>
        `${row.billing_profile_id}:${row.kind}:${row.normalized_value}`,
    ),
  };
}

export async function saveCloudProfile(
  client: SupabaseClient,
  profile: BillingProfile,
): Promise<void> {
  const { error } = await client.from("billing_profiles").upsert({
    id: profile.id,
    owner_user_id: profile.ownerUserId,
    display_name: profile.displayName,
    logo_path: profile.logoPath || null,
    address: profile.address || null,
    email: profile.email || null,
    phone: profile.phone || null,
    website: profile.website || null,
    registration_number: profile.registrationNumber || null,
    invoice_prefix: profile.invoicePrefix,
    default_payment_days: profile.defaultPaymentDays,
    brand_colour: profile.brandColour || null,
    payment_instructions: profile.paymentInstructions || null,
    archived_at: profile.archivedAt,
  });
  if (error) throw error;
}

export async function deleteCloudProfile(
  client: SupabaseClient,
  profile: BillingProfile,
): Promise<void> {
  const { data: receiptRows, error: receiptQueryError } = await client
    .from("invoice_receipts")
    .select("storage_path")
    .eq("billing_profile_id", profile.id);
  if (receiptQueryError) throw receiptQueryError;

  const receiptPaths = (receiptRows ?? []).map(
    (receipt) => receipt.storage_path as string,
  );
  if (receiptPaths.length > 0) {
    const { error: receiptStorageError } = await client.storage
      .from("invoice-receipts")
      .remove(receiptPaths);
    if (receiptStorageError) throw receiptStorageError;
  }

  const logoFolder = `${profile.ownerUserId}/${profile.id}`;
  const { data: logoObjects, error: listError } = await client.storage
    .from("billing-logos")
    .list(logoFolder, {
      limit: 1000,
      offset: 0,
      sortBy: { column: "name", order: "asc" },
    });
  if (listError) throw listError;

  const logoPaths = (logoObjects ?? [])
    .filter((object) => object.id !== null)
    .map((object) => `${logoFolder}/${object.name}`);
  if (logoPaths.length > 0) {
    const { error: logoError } = await client.storage
      .from("billing-logos")
      .remove(logoPaths);
    if (logoError) throw logoError;
  }

  const { data, error } = await client
    .from("billing_profiles")
    .delete()
    .eq("id", profile.id)
    .eq("owner_user_id", profile.ownerUserId)
    .select("id")
    .maybeSingle();
  if (error) throw error;
  if (!data) throw new Error("Billing profile was not found or could not be deleted.");
}

export async function saveCloudClient(
  client: SupabaseClient,
  record: Client,
): Promise<void> {
  const { error } = await client.from("clients").upsert({
    id: record.id,
    owner_user_id: record.ownerUserId,
    billing_profile_id: record.billingProfileId,
    business_name: record.party.name,
    contact_name: record.party.contactName || null,
    email: record.party.email || null,
    phone: record.party.phone || null,
    address: record.party.address || null,
    registration_number: record.party.registrationNumber || null,
    last_used_at: record.lastUsedAt,
  });
  if (error) throw error;
}

export async function saveCloudInvoice(
  client: SupabaseClient,
  invoice: Invoice,
): Promise<void> {
  const { error } = await client.rpc("save_invoice_snapshot", {
    p_invoice: {
      id: invoice.id,
      owner_user_id: invoice.ownerUserId,
      billing_profile_id: invoice.billingProfileId,
      number: invoice.number,
      status: invoice.status,
      issue_date: invoice.issueDate,
      due_date: invoice.dueDate,
      purchase_order_number: invoice.purchaseOrderNumber || null,
      project_reference: invoice.projectReference || null,
      currency: invoice.currency,
      language: invoice.language,
      paper_size: invoice.paperSize,
      issuer_snapshot: invoice.issuer,
      client_snapshot: invoice.client,
      discount: invoice.discount,
      subtotal_minor: invoice.subtotalMinor,
      discount_minor: invoice.discountMinor,
      total_minor: invoice.totalMinor,
      client_notes: invoice.clientNotes || null,
      internal_notes: invoice.internalNotes || null,
      payment_instructions: invoice.paymentInstructions || null,
      terms: invoice.terms || null,
      authorised_name: invoice.authorisedName || null,
      paid_minor: invoice.paidMinor,
      balance_minor: invoice.balanceMinor,
      items: invoice.items.map((item, position) => ({
        ...item,
        position,
      })),
      payments: invoice.payments,
    },
  });
  if (error) throw error;
}

export async function deleteCloudInvoice(
  client: SupabaseClient,
  invoice: Invoice,
): Promise<void> {
  const storagePaths = invoice.receipts.map((receipt) => receipt.storagePath);
  if (storagePaths.length > 0) {
    const { error: storageError } = await client.storage
      .from("invoice-receipts")
      .remove(storagePaths);
    if (storageError) throw storageError;
  }

  const { error } = await client
    .from("invoices")
    .delete()
    .eq("id", invoice.id);
  if (error) throw error;
}

export async function uploadCloudReceipt(
  client: SupabaseClient,
  options: {
    invoice: Invoice;
    receiptId: string;
    file: File;
    mimeType: ReceiptMimeType;
    position: number;
  },
): Promise<ReceiptAttachment> {
  const extension = {
    "image/jpeg": "jpg",
    "image/png": "png",
    "image/webp": "webp",
  }[options.mimeType];
  const storagePath = `${options.invoice.ownerUserId}/${options.invoice.id}/${options.receiptId}.${extension}`;
  const { error: uploadError } = await client.storage
    .from("invoice-receipts")
    .upload(storagePath, options.file, {
      contentType: options.mimeType,
      upsert: false,
    });
  if (uploadError) throw uploadError;

  const { data, error: insertError } = await client
    .from("invoice_receipts")
    .insert({
      id: options.receiptId,
      owner_user_id: options.invoice.ownerUserId,
      billing_profile_id: options.invoice.billingProfileId,
      invoice_id: options.invoice.id,
      storage_path: storagePath,
      file_name: options.file.name.slice(0, 255),
      mime_type: options.mimeType,
      file_size: options.file.size,
      position: options.position,
    })
    .select("*")
    .single();
  if (insertError) {
    await client.storage.from("invoice-receipts").remove([storagePath]);
    throw insertError;
  }

  const row = data as ReceiptRow;
  return {
    id: row.id,
    ownerUserId: row.owner_user_id,
    billingProfileId: row.billing_profile_id,
    invoiceId: row.invoice_id,
    storagePath: row.storage_path,
    fileName: row.file_name,
    mimeType: row.mime_type,
    fileSize: row.file_size,
    position: row.position,
    createdAt: row.created_at,
  };
}

export async function deleteCloudReceipt(
  client: SupabaseClient,
  receipt: ReceiptAttachment,
): Promise<void> {
  const { error: storageError } = await client.storage
    .from("invoice-receipts")
    .remove([receipt.storagePath]);
  if (storageError) throw storageError;

  const { data, error } = await client
    .from("invoice_receipts")
    .delete()
    .eq("id", receipt.id)
    .eq("owner_user_id", receipt.ownerUserId)
    .select("id")
    .maybeSingle();
  if (error) throw error;
  if (!data) throw new Error("Receipt was not found or could not be deleted.");
}

export async function deleteCloudClient(
  client: SupabaseClient,
  clientId: string,
): Promise<void> {
  const { error } = await client.from("clients").delete().eq("id", clientId);
  if (error) throw error;
}

export async function suppressCloudHistory(
  client: SupabaseClient,
  options: {
    ownerUserId: string;
    billingProfileId: string;
    kind: "description" | "unit" | "client_notes" | "internal_notes";
    value: string;
  },
): Promise<void> {
  const { error } = await client.from("invoice_text_suppressions").upsert(
    {
      owner_user_id: options.ownerUserId,
      billing_profile_id: options.billingProfileId,
      kind: options.kind,
      normalized_value: options.value.trim().toLocaleLowerCase("en"),
    },
    {
      onConflict: "billing_profile_id,kind,normalized_value",
    },
  );
  if (error) throw error;
}
